import { z } from "zod";
import type { Money, Price, Qty } from "./money";

/**
 * A trading account. Ours, not a broker's — see
 * ADR-0001.
 */

export const ACCOUNT_MODES = ["paper", "live"] as const;
export type AccountMode = (typeof ACCOUNT_MODES)[number];

export const ACCOUNT_STATUSES = ["active", "restricted", "closed"] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

export type TradingAccount = {
  id: string;
  userId: string;
  /**
   * `live` accounts exist in the model so the type doesn't have to change when
   * real execution arrives, but every route that could act on one returns 501.
   * A hard guard, not a config value — see ADR-0006.
   */
  mode: AccountMode;
  status: AccountStatus;
  baseCurrency: string;
  createdAt: Date;
};

export type Position = {
  accountId: string;
  symbol: string;
  /** Signed: negative is short. Shorting is not enabled in the first pass. */
  qty: Qty;
  avgEntryPrice: Price;
  costBasis: Money;
};

/**
 * The derived financial state of an account at a point in time.
 *
 * Every field here is computed — from the ledger for cash, from positions and
 * current quotes for market value. Nothing in this type is stored as a mutable
 * number, which is the property that makes it always reconstructible.
 */
export type AccountSnapshot = {
  accountId: string;
  /** sum(ledger_entries where account = 'cash') */
  cash: Money;
  /** sum(qty * last) over positions */
  longMarketValue: Money;
  /** cash + longMarketValue */
  equity: Money;
  /** What may still be committed to new orders: cash minus open-order reservations. */
  buyingPower: Money;
  positions: readonly Position[];
  asOf: Date;
};

export const openAccountRequestSchema = z
  .object({
    /** Starting simulated cash, decimal string. Defaults to 100,000. */
    initial_cash: z
      .string()
      .regex(/^\d+(\.\d+)?$/, "must be a non-negative decimal string")
      .default("100000.0000"),
  })
  .strict();

export type OpenAccountRequest = z.infer<typeof openAccountRequestSchema>;

/**
 * Per-user limits every order submission is checked against, human or agent.
 * Enforced in `packages/oms/src/policy.ts` before AccountDO sees the order.
 */
export type TradingPolicy = {
  userId: string;
  /** Blocks every submission for this user. */
  killSwitch: boolean;
  maxNotionalPerOrder: Money;
  /** Limits one autonomous DCA notional or band tranche. */
  maxIntradayNotionalPerOrder: Money;
  maxPositionNotional: Money;
  maxOrdersPerDay: number;
  /** Empty means "all symbols permitted". */
  symbolAllowlist: readonly string[];
  symbolDenylist: readonly string[];
  /** Agents may be restricted more tightly than the human who owns the account. */
  agentsMayTrade: boolean;
};

export const DEFAULT_TRADING_POLICY: Omit<TradingPolicy, "userId"> = {
  killSwitch: false,
  maxNotionalPerOrder: 25_000_0000n as Money, // $25,000.0000
  maxIntradayNotionalPerOrder: 100_0000n as Money, // $100.0000
  maxPositionNotional: 100_000_0000n as Money, // $100,000.0000
  maxOrdersPerDay: 500,
  symbolAllowlist: [],
  symbolDenylist: [],
  agentsMayTrade: true,
};
